import type { HTMLAttributes, ReactNode } from 'react';
import './Card.css';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Remove body padding — for cards that hold a flush table or list. */
  tight?: boolean;
}

function CardRoot({ tight = false, className, children, ...rest }: CardProps): JSX.Element {
  const cls = ['dsr-card', tight ? 'is-tight' : '', className].filter(Boolean).join(' ');
  return (
    <div className={cls} {...rest}>
      {children}
    </div>
  );
}

function CardHeader({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div className={['dsr-card-head', className].filter(Boolean).join(' ')} {...rest}>
      {children}
    </div>
  );
}

function CardTitle({ className, children, ...rest }: HTMLAttributes<HTMLHeadingElement>): JSX.Element {
  return (
    <h3 className={['dsr-card-title', className].filter(Boolean).join(' ')} {...rest}>
      {children}
    </h3>
  );
}

function CardBody({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div className={['dsr-card-body', className].filter(Boolean).join(' ')} {...rest}>
      {children}
    </div>
  );
}

export const Card = Object.assign(CardRoot, {
  Header: CardHeader,
  Title: CardTitle,
  Body: CardBody,
});

export type KpiTone = 'neutral' | 'success' | 'warn' | 'danger';

const KPI_TONE_CLASS: Record<KpiTone, string> = {
  neutral: '',
  success: 'kpi-success',
  warn: 'kpi-warn',
  danger: 'kpi-danger',
};

export interface KpiCardProps {
  label: ReactNode;
  value: ReactNode;
  tone?: KpiTone;
  meta?: ReactNode;
}

// Emits the existing `.kpi-card` markup used on the dashboard — drop-in.
export function KpiCard({ label, value, tone = 'neutral', meta }: KpiCardProps): JSX.Element {
  return (
    <div className="kpi-card">
      <span className="kpi-label">{label}</span>
      <span className={['kpi-value', KPI_TONE_CLASS[tone]].filter(Boolean).join(' ')}>{value}</span>
      {meta && <span className="kpi-meta">{meta}</span>}
    </div>
  );
}
